import {
	ApolloClient,
	ApolloLink,
	defaultDataIdFromObject,
	from,
	HttpLink,
	InMemoryCache,
} from '@apollo/client';
import { getItemFromLocalStorage } from '@utils/localStorage';
import config from '@/config';
import { fetchSubDomain } from '@utils/urlHelpers';
import _ from 'lodash';

const subDomain = fetchSubDomain();

export const mergeFunc = ({ existing, incoming, readField, args }) => {
	const after = _.get(args, 'filters.after');

	if (after) {
		const existingEdge = readField('edges', existing) ?? [];
		const incomingEdges = readField('edges', incoming) ?? [];
		const pageInfo = readField('pageInfo', incoming);

		const mergedData = _.uniqBy([...existingEdge, ...incomingEdges], (data) => {
			const node = readField('node', data);
			return readField('id', node);
		});

		return {
			...incoming,
			pageInfo,
			edges: mergedData,
		};
	}

	return incoming;
};

const dataIdFromObject = (responseObject) => {
	switch (responseObject.__typename) {
		case 'PermissionValue': {
			//this is done because sometime name and roleId is not comming from graphql only id is there for e.g. permission child permissions
			return !_.isNil(_.get(responseObject, 'name'))
				? `PermissionValue:${responseObject.name}:${responseObject.roleId}`
				: defaultDataIdFromObject(responseObject);
		}
		case 'FeatureFlag':
			return `FeatureFlag:${responseObject.featureKey}`;
		// case 'FeatureFlagSettingValue': {
		// 	//this is done because sometime name and roleId is not comming from graphql only id is there for e.g. feature flag setting value
		// 	return `FeatureFlagSettingValue:${responseObject.userTypeId}:${
		// 		_.isNil(responseObject.roleId) ? 'null' : responseObject.roleId
		// 	}`;
		// }

		default:
			return defaultDataIdFromObject(responseObject);
	}
};

const httpLink = new HttpLink({
	uri: `${config.backendUrl}/graphql`,
});

const authMiddleware = new ApolloLink((operation, forward) => {
	// add the authorization to the headers
	operation.setContext(({ headers = {} }) => {
		const userData = getItemFromLocalStorage(subDomain);
		return {
			headers: {
				...headers,
				authorization: userData?.token,
			},
		};
	});

	return forward(operation);
});

const typePolicies = {
	Section: {
		fields: {
			students: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			homework: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Announcement: {
		fields: {
			recipients: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Staff: {
		fields: {
			staffAiChats: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			chats: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			exams: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			downloadJobs: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			driveItems: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			announcements: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			posts: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			complaints: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			excuses: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Campus: {
		fields: {
			sections: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			exams: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			staff: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			students: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			gatePasses: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			threads: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Thread: {
		fields: {
			recipients: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Student: {
		fields: {
			gatePasses: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			examConnection: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			studentThreads: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Grade: {
		fields: {
			students: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	ReportRun: {
		fields: {
			studentReports: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.sortBy(_.keys(_.omit(filters, 'after')))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
	Chat: {
		fields: {
			messages: {
				// Drop only the pagination args so every page of the same
				// filtered list shares one cache entry.
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return [
						'filters',
						_.keys(_.omit(filters, 'first', 'last', 'after', 'before')),
					];
				},
				merge: (existing, incoming, { readField, args }) => {
					const filters = _.get(args, 'filters');
					const after = _.get(filters, 'after');
					const before = _.get(filters, 'before');

					const incomingEdges = readField('edges', incoming) ?? [];

					// The cache is stored OLDEST-first; the server always sends newest-first.
					const toOldestFirst = (edges) => [...edges].reverse();

					if (!after && !before) {
						return { ...incoming, edges: toOldestFirst(incomingEdges) };
					}

					const existingEdges = readField('edges', existing) ?? [];
					const existingPageInfo = readField('pageInfo', existing) ?? {};
					const incomingPageInfo = readField('pageInfo', incoming) ?? {};

					const isOlderPage = Boolean(after);
					const pageEdges = toOldestFirst(incomingEdges);

					const combined = isOlderPage
						? [...pageEdges, ...existingEdges]
						: [...existingEdges, ...pageEdges];

					const edges = _.uniqBy(combined, (edge) =>
						readField('id', readField('node', edge))
					);

					// Keep each scroll direction's cursors independent.
					const pageInfo = isOlderPage
						? {
								...existingPageInfo,
								hasNextPage: incomingPageInfo.hasNextPage,
								endCursor: incomingPageInfo.endCursor,
							}
						: {
								...existingPageInfo,
								hasPreviousPage: incomingPageInfo.hasPreviousPage,
								startCursor: incomingPageInfo.startCursor,
							};

					return { ...incoming, edges, pageInfo };
				},
			},
		},
	},
	Exam: {
		keyFields: (object) => {
			if (object.studentMark !== undefined) {
				return false;
			}
			return ['id'];
		},
	},
	CustomField: {
		keyFields: false,
	},
	FeatureFlagSettingValue: {
		keyFields: false,
	},
	ExamSubjectAttendance: {
		keyFields: false,
	},
	Organization: {
		fields: {
			sections: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			locations: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			staff: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			students: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			messagingStaff: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			nonTechStaff: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
			feePayments: {
				keyArgs: (args) => {
					const filters = _.get(args, 'filters');

					return ['filters', _.keys(_.omit(filters, 'after'))];
				},
				merge: (existing, incoming, { readField, args }) => {
					return mergeFunc({ existing, incoming, readField, args });
				},
			},
		},
	},
};

const client = new ApolloClient({
	cache: new InMemoryCache({ dataIdFromObject, typePolicies }),
	link: from([authMiddleware, httpLink]),
});

export default client;
